// src/components/SmartRoot.jsx
// Smart root component that renders Dashboard if authenticated, or UpdatedWelcomePage if not
import React, { useContext, useState, useEffect, lazy, Suspense } from 'react';
import { UserContext } from '../context/UserContext';
import UpdatedWelcomePage from '../pagesMVP1010/UpdatedWelcomePage.js';
import RequireDailyCheckIn from '../routeMVP1010/RequireDailyCheckIn';
import ProtectedWithConsent from './ProtectedWithConsent';
import api from '../utils/api';

// Lazy load both dashboard versions for A/B testing
const Dashboard = lazy(() => import('../pagesMVP1010/BloomPalAfterMoodcall.jsx'));
const DashboardV2 = lazy(() => import('../pagesMVP1010/BloomPalAfterMoodcallV2.jsx'));

/**
 * Get session ID for exposure logging
 */
const getSessionId = () => {
  let sessionId = sessionStorage.getItem('bp_session_id');
  if (!sessionId) {
    sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    sessionStorage.setItem('bp_session_id', sessionId);
  }
  return sessionId;
};

/**
 * Log home dashboard exposure to backend
 */
const logHomeDashboardExposure = async (version, source, forceVersionParam = '', assignedVersionLocal = '') => {
  try {
    const sessionId = getSessionId();
    const today = new Date().toISOString().split('T')[0];
    const dedupeKey = `home_exposed_${version}_${today}`;
    
    // Check if we've already logged today for this version
    if (sessionStorage.getItem(dedupeKey)) {
      return;
    }
    
    const payload = {
      session_id: sessionId,
      page: 'home',
      version: version,
      source: source,
      force_version_param: forceVersionParam,
      assigned_version_local: assignedVersionLocal,
      user_agent: navigator.userAgent,
    };
    
    await api.post('/api/analytics/home-dashboard-exposure/', payload);
    
    // Mark as logged for today
    sessionStorage.setItem(dedupeKey, 'true');
  } catch (error) {
    console.warn('[A/B Test] Failed to log exposure:', error);
    // Don't throw - exposure logging is non-critical
  }
};

/**
 * Get home dashboard version from backend or fallback
 */
const getHomeVersionFromBackend = async () => {
  try {
    const response = await api.get('/api/me/home-dashboard-version/');
    return {
      version: response.data.version,
      source: response.data.source,
      fromBackend: true,
    };
  } catch (error) {
    console.warn('[A/B Test] Backend endpoint failed, using fallback:', error);
    // Fallback to localStorage
    const existingVersion = localStorage.getItem('bp_home_version');
    if (existingVersion === 'v1' || existingVersion === 'v2') {
      return {
        version: existingVersion,
        source: 'frontend_fallback',
        fromBackend: false,
      };
    }
    // Last resort: random assignment
    const randomVersion = Math.random() < 0.5 ? 'v1' : 'v2';
    localStorage.setItem('bp_home_version', randomVersion);
    return {
      version: randomVersion,
      source: 'frontend_fallback',
      fromBackend: false,
    };
  }
};

export default function SmartRoot() {
  const { authToken } = useContext(UserContext);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [dashboardVersion, setDashboardVersion] = useState(null);
  const [versionSource, setVersionSource] = useState(null);
  const [showDebugBadge, setShowDebugBadge] = useState(false);

  useEffect(() => {
    // Check for debug query param
    const urlParams = new URLSearchParams(window.location.search);
    setShowDebugBadge(urlParams.get('debug_ab') === '1');
    
    // Check if user is authenticated by checking for authToken
    const token = localStorage.getItem('authToken');
    const authenticated = !!(token || authToken);
    setIsAuthenticated(authenticated);
    
    // Determine dashboard version for A/B testing
    if (authenticated) {
      const urlParams = new URLSearchParams(window.location.search);
      const forceVersion = urlParams.get('force_version');
      
      // Handle force_version for developers (local override only, don't persist)
      if (forceVersion === 'v1' || forceVersion === 'v2') {
        setDashboardVersion(forceVersion);
        setVersionSource('forced_local');
        // Log exposure with forced source
        logHomeDashboardExposure(forceVersion, 'forced_local', forceVersion, '');
        setLoading(false);
        return;
      }
      
      // Get version from backend
      getHomeVersionFromBackend().then(({ version, source, fromBackend }) => {
        setDashboardVersion(version);
        setVersionSource(source);
        
        // Log exposure
        logHomeDashboardExposure(version, source, '', fromBackend ? '' : localStorage.getItem('bp_home_version') || '');
        
        setLoading(false);
      }).catch((error) => {
        console.error('[A/B Test] Error getting version:', error);
        // Fallback
        const fallbackVersion = localStorage.getItem('bp_home_version') || (Math.random() < 0.5 ? 'v1' : 'v2');
        setDashboardVersion(fallbackVersion);
        setVersionSource('frontend_fallback');
        logHomeDashboardExposure(fallbackVersion, 'frontend_fallback', '', fallbackVersion);
        setLoading(false);
      });
    } else {
      setLoading(false);
    }
  }, [authToken]);

  // Show loading state briefly while checking auth
  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        flexDirection: 'column',
        gap: '1rem'
      }}>
        <div style={{
          width: '40px',
          height: '40px',
          border: '4px solid #f3f3f3',
          borderTop: '4px solid #10B981',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }} />
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  // If authenticated, show Dashboard with consent and check-in guards
  // Use A/B testing to determine which version to show
  if (isAuthenticated && dashboardVersion) {
    const SelectedDashboard = dashboardVersion === 'v2' ? DashboardV2 : Dashboard;
    
    return (
      <>
        {showDebugBadge && versionSource && (
          <div style={{
            position: 'fixed',
            top: '10px',
            right: '10px',
            background: 'rgba(0, 0, 0, 0.8)',
            color: 'white',
            padding: '8px 12px',
            borderRadius: '4px',
            fontSize: '12px',
            zIndex: 9999,
            fontFamily: 'monospace',
          }}>
            Home {dashboardVersion} (source: {versionSource})
          </div>
        )}
        <ProtectedWithConsent>
          <RequireDailyCheckIn moodcallPath="/MoodCall" useLocalFastPass={true}>
            <Suspense fallback={
              <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                minHeight: '100vh',
                flexDirection: 'column',
                gap: '1rem'
              }}>
                <div style={{
                  width: '40px',
                  height: '40px',
                  border: '4px solid #f3f3f3',
                  borderTop: '4px solid #10B981',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite'
                }} />
                <style>{`
                  @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                  }
                `}</style>
              </div>
            }>
              <SelectedDashboard />
            </Suspense>
          </RequireDailyCheckIn>
        </ProtectedWithConsent>
      </>
    );
  }

  // If not authenticated, show the landing page
  return <UpdatedWelcomePage />;
}

